import AccountSelectionGrid from "#components/SelectionGrid/index.js";
import DropdownSelect from "#components/DropdownSelect/index.js";
import MDButton from "#components/MDButton/index.js";
import TranslatableTextField from "#components/TranslatableTextField/index.js";
import { Delete } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import { SortableListContainer } from "layouts/logsheets/components/SortableListContainer";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import accountsService from "services/accounts-service";
import unitsService from "services/units-service";
import BaseEntitySelectionGrid from "#components/BaseEntitySelectionGrid/index.js";

const validation = {
  number: [
    {
      key: "unitId",
      optionsKey: "units",
      label: "Unit",
      type: "dropdown",
      required: false,
      xs: 4,
      crud: (state, setState) => {
        return {
          create: async (data) => {
            let [ok, response] = await unitsService.createUnit({
              name: data.label,
            });
            return ok;
          },
          update: async (id, data) => {
            let [ok, response] = await unitsService.updateUnit(id, {
              name: data.label,
            });
            return ok;
          },
          remove: async (id) => {
            let [ok, response] = await unitsService.removeUnit(id);
            return ok;
          },
          refresh: async () => {
            let [ok, data] = await unitsService.getUnits();
            if (ok) setState(data);
            return ok;
          },
        };
      },
    },
    {
      key: "min",
      label: "Minimum",
      type: "number",
      withUnit: "unitId",
      xs: 4,
    },
    {
      key: "max",
      label: "Maximum",
      type: "number",
      withUnit: "unitId",
      xs: 4,
    },
  ],
  text: [
    {
      key: "min",
      label: "Minimum Characters",
      type: "number",
      xs: 6,
    },
    {
      key: "max",
      label: "Maximum Characters",
      type: "number",
      xs: 6,
    },
  ],
  datetime: [
    // {
    //   key: "min",
    //   optionsKey: "relativeDates",
    //   label: "Minimum Date Limit",
    //   type: "datetime",
    //   required: false,
    //   xs: 6,
    // },
    // {
    //   key: "max",
    //   optionsKey: "relativeDates",
    //   label: "Maximum Date Limit",
    //   type: "datetime",
    //   required: false,
    //   xs: 6,
    // },
  ],
  dropdown: [
    { key: "options", label: "Dropdown Options", type: "optionsCRUD", xs: 12 },
  ],
  boolean: [],
  "datetime-auto": [],
  audio: [],
  image: [],
};

export default function PropertiesDialog({
  isOpen,
  onSubmit,
  data,
  mode,
  onClose,
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [updates, setUpdates] = React.useState({});
  const [initialProperty, setInitialProperty] = React.useState({
    name: { en: "" },
    referenceCode: "",
  });
  const [options, setOptions] = React.useState({
    accounts: [],
    baseEntities: [],
  });
  const { label } = useLanguage();
  const [error, setError] = React.useState(null);
  const [newOptionLabel, setNewOptionLabel] = useState("");

  const property = React.useMemo(
    () => ({
      ...{
        ...initialProperty,
        options: initialProperty?.options?.map((option) => {
          return {
            label: option.name,
            id: option.value,
            sequence: option.sequence,
          };
        }),
      },
      ...updates,
    }),
    [initialProperty, updates],
  );

  const sortedOptions = React.useMemo(() => {
    return [...(property.options || [])].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
    );
  }, [property.options]);

  useEffect(() => {
    setUpdates({});
    setNewOptionLabel("");
  }, [initialProperty?.id]);

  const isDirty = Object.keys(updates).length > 0;

  const handleAddOption = () => {
    if (!newOptionLabel.trim()) return;
    const currentOptions = property.options || [];

    const updatedOptions = [
      ...currentOptions,
      {
        id:
          currentOptions.reduce((prev, curr) => {
            if (curr.id > prev) {
              prev = curr.id;
            }
            return prev;
          }, 1) + 1,
        label: newOptionLabel.trim(),
        sequence: currentOptions.length,
      },
    ];

    setUpdates((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
    setNewOptionLabel("");
  };

  const handleDeleteOption = (optionId) => {
    const currentOptions = property.options || [];

    const updatedOptions = currentOptions
      .filter((opt) => opt.id !== optionId)
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((opt, index) => ({ ...opt, sequence: index }));

    setUpdates((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  async function fetch() {
    let [ok, data] = await accountsService.getAccounts({ raw: true });
    if (ok) {
      setOptions({ ...options, accounts: data });
    }

    [ok, data] = await unitsService.getUnits();
    if (ok) {
      setOptions({ ...options, units: data });
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setInitialProperty({
      name: { en: "" },
    });
  }, [isOpen, mode]);

  React.useEffect(() => {
    if (data) setInitialProperty({ ...data, name: data?.allLanguageName });
  }, [data, mode, isOpen]);

  React.useEffect(() => {
    setError(null);
    if (isOpen) fetch();
  }, [mode, isOpen]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const dropdownOptions = React.useMemo(() => {
    return {
      accounts:
        options.accounts?.map((v) => ({ id: v.id, label: v.name })) || [],
      baseEntities: options.baseEntities?.map((v) => ({
        id: v.id,
        label: v.name,
      })),
      units: options.units?.map((v) => ({ id: v.id, label: v.name })) || [],
      genericPropertyTypes: [
        { id: "number", label: "Number" },
        { id: "boolean", label: "Yes / No" },
        { id: "datetime", label: "Date / Time" },
        { id: "datetime-auto", label: "Automatic date and time" },
        { id: "audio", label: "Audio" },
        { id: "image", label: "Picture" },
        { id: "dropdown", label: "Dropdown" },
        { id: "text", label: "Text" },
      ],
    };
  }, [options]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(property);

          if (result.error) {
            setError(result.error);
          } else {
            setError(null);
            handleClose();
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {mode == "edit" ? label("edit-property") : label("create-property")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontSize: "14px", mb: 0 }}>
              Info
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TranslatableTextField
              autoFocus
              required
              id="name"
              name="name"
              label={label("name-singular")}
              type="text"
              values={property.name}
              onChange={(e) =>
                setUpdates((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <DropdownSelect
              required
              label={"Type"}
              initialValue={property.generic}
              items={dropdownOptions.genericPropertyTypes}
              onChange={(value) => {
                setUpdates((prev) => ({
                  ...prev,
                  generic: value?.id || "",
                  ...(value?.id !== property.generic && {}),
                }));
              }}
              fullWidth
            />
          </Grid>

          <AccountSelectionGrid
            accountId={property.accountId}
            setAccountId={(accountId) => {
              setUpdates((prev) => ({ ...prev, accountId }));
            }}
          />

          <BaseEntitySelectionGrid
            baseEntityId={property.baseEntityId}
            setBaseEntityId={(baseEntityId) => {
              setUpdates((prev) => ({ ...prev, baseEntityId }));
            }}
          />

          {property.generic &&
            validation[property.generic] &&
            validation[property.generic].length > 0 && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ fontSize: "14px", mb: 0 }}>
                  Validation
                </Typography>
              </Grid>
            )}

          {property.generic
            ? validation[property.generic]?.map((field) => {
                return (
                  <Grid item xs={field.xs || 6} key={"validation-" + field.key}>
                    {field.type === "dropdown" ? (
                      <DropdownSelect
                        required={field.required}
                        label={field.label}
                        initialValue={property[field.key]}
                        onChange={(value) => {
                          setUpdates((prev) => ({
                            ...prev,
                            [field.key]: value?.id || null,
                          }));
                        }}
                        items={dropdownOptions[field.optionsKey] || []}
                        fullWidth
                        crud={
                          field.crud
                            ? field.crud(
                                dropdownOptions[field.optionsKey],
                                (newValue) => {
                                  console.log("NEW VALUE: ", newValue);
                                  setOptions((options) => ({
                                    ...options,
                                    [field.optionsKey]: newValue,
                                  }));
                                },
                              )
                            : null
                        }
                        {...field.props}
                      />
                    ) : null}

                    {field.type === "number" ? (
                      <TextField
                        key={`validation-number-${field.key}-${field.withUnit ? property.unitId : null}`}
                        label={field.label}
                        type="number"
                        fullWidth
                        value={property[field.key] || ""}
                        onChange={(e) => {
                          setUpdates((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }));
                        }}
                        InputProps={{
                          endAdornment:
                            field.withUnit &&
                            dropdownOptions.units?.find(
                              (unit) => unit.id == property.unitId,
                            )?.label ? (
                              <InputAdornment position="end">
                                <Typography
                                  sx={{ fontSize: "14px", fontWeight: "400" }}
                                >
                                  {
                                    dropdownOptions.units?.find(
                                      (unit) => unit.id == property.unitId,
                                    )?.label
                                  }
                                </Typography>
                              </InputAdornment>
                            ) : null,
                        }}
                      />
                    ) : null}

                    {field.type === "optionsCRUD" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <TextField
                            fullWidth
                            label="New Option Label"
                            value={newOptionLabel}
                            onChange={(e) => setNewOptionLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddOption();
                              }
                            }}
                          />
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            onClick={handleAddOption}
                            disabled={!newOptionLabel}
                          >
                            Add
                          </MDButton>
                        </Box>

                        <SortableListContainer
                          title="Options"
                          items={sortedOptions.map((option) => ({
                            ...option,
                            name: option.label,
                          }))}
                          onChange={(updatedItems) => {
                            setUpdates((prev) => ({
                              ...prev,
                              options: updatedItems.map((item) => ({
                                ...item,
                                label: item.name,
                              })),
                            }));
                          }}
                          emptyMessage="No options defined yet"
                          renderItemExtra={(item) => (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteOption(item.id)}
                              sx={{ ml: "auto" }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                          includeCheckboxes={false}
                        />
                      </Box>
                    ) : null}
                  </Grid>
                );
              })
            : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {mode == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
